'use client';

import { CHAT_LABELS } from '@/app/lib/guideChat/labels';
import type { OpenerResponse } from '@/app/lib/guideChat/types';
import { OPENER_SUGGESTIONS } from './api';

interface Props {
  opener: OpenerResponse;
  placement: 'bottom' | 'top';
  onDismiss: () => void;
  onAccept: (suggestion?: string) => void;
}

export default function ProactiveOpener({ opener, placement, onDismiss, onAccept }: Props) {
  const position = placement === 'top' ? 'top-4 left-4 right-4' : 'bottom-24 left-4 right-4 md:left-auto md:right-6';
  return (
    <section
      role="region"
      aria-label={CHAT_LABELS.openerRegion}
      aria-live="polite"
      className={`fixed z-[60] md:w-[360px] rounded-2xl border border-rm-blue-light bg-white p-4 shadow-lg ${position}`}
    >
      <div className="flex items-start gap-3">
        <span aria-hidden="true" className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-rm-blue-deep text-white">
          <i className="fa-solid fa-comment-question text-base" />
        </span>
        <p className="min-w-0 flex-1 text-sm leading-relaxed text-pale">
          {/* Only the first line makes room for the close button, so the opener still holds in three lines. */}
          <span aria-hidden="true" className="float-right h-5 w-7" />
          {opener.message}
        </p>
        <button
          type="button"
          onClick={onDismiss}
          aria-label={CHAT_LABELS.openerClose}
          title={CHAT_LABELS.openerCloseTitle}
          className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full text-rm-gray-dark hover:bg-rm-gray-rule hover:text-pale focus:outline-none focus-visible:ring-2 focus-visible:ring-rm-blue-deep"
        >
          <span aria-hidden="true" className="text-xl leading-none">×</span>
        </button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {opener.suggestions.slice(0, OPENER_SUGGESTIONS).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onAccept(s)}
            className="rounded-full border border-rm-blue-deep bg-white px-3 py-1.5 text-left text-xs font-medium text-rm-blue-deep hover:bg-rm-blue-pale focus:outline-none focus-visible:ring-2 focus-visible:ring-rm-blue-deep"
          >
            {s}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onAccept()}
          className="rounded-full bg-rm-blue-deep px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-rm-blue-deep"
        >
          {CHAT_LABELS.openerAsk}
        </button>
      </div>
    </section>
  );
}
